import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { useI18n } from "../lib/i18n";
import { Inbox, Check, X } from "lucide-react";

export default function Approvals() {
  const { t } = useI18n();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = ["pto", "reimb", "ts", "billing"].includes(searchParams.get("tab")) ? searchParams.get("tab") : "pto";
  const [tab, setTab] = useState(initialTab);
  const openId = searchParams.get("open");
  const [pto, setPto] = useState([]);
  const [reimb, setReimb] = useState([]);
  const [ts, setTs] = useState([]);
  const [billing, setBilling] = useState([]);
  const [comment, setComment] = useState({});
  const rowRefs = useRef({});

  const load = async () => {
    const [a, b, c, d] = await Promise.all([
      api.get("/pto/pending"),
      api.get("/reimbursements/pending"),
      api.get("/timesheets/pending"),
      api.get("/billing-approvals/pending"),
    ]);
    setPto(a.data);
    setReimb(b.data);
    setTs(c.data);
    setBilling(d.data || []);
  };
  useEffect(() => { load(); }, []);

  // React when URL changes (from cockpit navigation to specific items)
  useEffect(() => {
    const qTab = searchParams.get("tab");
    if (qTab && ["pto", "reimb", "ts", "billing"].includes(qTab) && qTab !== tab) {
      setTab(qTab);
    }
  }, [searchParams]); // eslint-disable-line

  // Scroll to highlighted row once data is loaded
  useEffect(() => {
    if (!openId) return;
    const el = rowRefs.current[openId];
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [openId, pto, reimb, ts, billing, tab]);

  const switchTab = (newTab) => {
    setTab(newTab);
    // keep `open` only if URL had no `open` for this tab; clear it on manual tab change
    const next = new URLSearchParams(searchParams);
    next.set("tab", newTab);
    next.delete("open");
    setSearchParams(next, { replace: true });
  };

  const decide = async (kind, id, decision) => {
    const ep = kind === "pto"
      ? `/pto/${id}/decide`
      : kind === "reimb"
      ? `/reimbursements/${id}/decide`
      : kind === "billing"
      ? `/billing-approvals/${id}/decide`
      : `/timesheets/${id}/decide`;
    try {
      await api.post(ep, { decision, comment: comment[id] || "" });
      await load();
    } catch (err) {
      alert(err?.response?.data?.detail || "Error");
    }
  };

  const cardCls = (id) => `sw-card grid grid-cols-1 lg:grid-cols-[2fr_1fr_auto] gap-4 items-start ${openId === id ? "ring-2 ring-[#475569] shadow-lg" : ""}`;


  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-[14px] bg-[#e2e8f0] text-[#475569] flex items-center justify-center">
          <Inbox size={22} />
        </div>
        <div>
          <h1 className="sw-heading text-[28px]">{t("approvals.title")}</h1>
          <p className="text-[14px] text-[#747474]">{t("approvals.waiting_n", { n: pto.length + reimb.length + ts.length + billing.length })}</p>
        </div>
      </div>

      <div className="inline-flex bg-white border border-[#eeeded] rounded-full p-1 w-fit flex-wrap">
        <button onClick={() => switchTab("pto")} data-testid="approvals-tab-pto" className={`px-5 py-2 rounded-full text-[13px] font-medium transition-all ${tab === "pto" ? "bg-[#475569] text-white" : "text-[#747474]"}`}>
          {t("approvals.pto")} <span className="ml-1.5 opacity-70">{pto.length}</span>
        </button>
        <button onClick={() => switchTab("reimb")} data-testid="approvals-tab-reimb" className={`px-5 py-2 rounded-full text-[13px] font-medium transition-all ${tab === "reimb" ? "bg-[#475569] text-white" : "text-[#747474]"}`}>
          {t("approvals.reimb")} <span className="ml-1.5 opacity-70">{reimb.length}</span>
        </button>
        <button onClick={() => switchTab("ts")} data-testid="approvals-tab-ts" className={`px-5 py-2 rounded-full text-[13px] font-medium transition-all ${tab === "ts" ? "bg-[#475569] text-white" : "text-[#747474]"}`}>
          Timesheets <span className="ml-1.5 opacity-70">{ts.length}</span>
        </button>
        <button onClick={() => switchTab("billing")} data-testid="approvals-tab-billing" className={`px-5 py-2 rounded-full text-[13px] font-medium transition-all ${tab === "billing" ? "bg-[#475569] text-white" : "text-[#747474]"}`}>
          {t("approvals.billing")} <span className="ml-1.5 opacity-70">{billing.length}</span>
        </button>
      </div>

      <div className="flex flex-col gap-4">
        {(tab === "pto" ? pto : tab === "reimb" ? reimb : tab === "billing" ? billing : ts).length === 0 && (
          <div className="sw-card text-center py-12 text-[#747474]">{t("approvals.empty")}</div>
        )}
        {tab === "pto" && pto.map((it) => (
          <div key={it.id} ref={(el) => (rowRefs.current[it.id] = el)} data-testid={`approval-pto-${it.id}`} className={cardCls(it.id)}>
            <div>
              <div className="text-[11px] uppercase tracking-wider text-[#475569] mb-1">{it.type}</div>
              <div className="font-medium text-[15px]">
                {it.user_name}
                {it.external && (
                  <span data-testid={`external-badge-${it.id}`} className="ml-2 align-middle inline-flex px-2 py-0.5 rounded-full bg-[#fcf3e2] text-[#a38d5b] text-[10px] font-semibold uppercase tracking-wide">
                    {t("ext.provider_badge", { name: it.provider_tenant_name || "" })}
                  </span>
                )}
              </div>
              <div className="text-[13px] text-[#747474]">{it.start_date} → {it.end_date} · {it.days} {t("dash.days")}</div>
              {it.reason && <div className="text-[13px] text-[#323232] mt-2 italic">&ldquo;{it.reason}&rdquo;</div>}
            </div>
            <input
              placeholder={t("approvals.comment")}
              value={comment[it.id] || ""}
              onChange={(e) => setComment({ ...comment, [it.id]: e.target.value })}
              className="sw-input"
            />
            <div className="flex gap-2">
              <button data-testid={`approve-pto-${it.id}`} onClick={() => decide("pto", it.id, "approved")} className="sw-btn-primary !bg-[#008932] hover:!bg-[#0b430b]">
                <Check size={14} /> {t("approvals.approve")}
              </button>
              <button data-testid={`reject-pto-${it.id}`} onClick={() => decide("pto", it.id, "rejected")} className="sw-btn-secondary !text-[#881a27] !border-[#e5c1c1]">
                <X size={14} /> {t("approvals.reject")}
              </button>
            </div>
          </div>
        ))}
        {tab === "reimb" && reimb.map((it) => (
          <div key={it.id} ref={(el) => (rowRefs.current[it.id] = el)} data-testid={`approval-reimb-${it.id}`} className={cardCls(it.id)}>
            <div>
              <div className="text-[11px] uppercase tracking-wider text-[#475569] mb-1">{it.category}</div>
              <div className="font-medium text-[15px]">
                {it.user_name} · ${parseFloat(it.amount).toFixed(2)}
                {it.external && (
                  <span data-testid={`external-badge-reimb-${it.id}`} className="ml-2 align-middle inline-flex px-2 py-0.5 rounded-full bg-[#fcf3e2] text-[#a38d5b] text-[10px] font-semibold uppercase tracking-wide">
                    {t("ext.provider_badge", { name: it.provider_tenant_name || "" })}
                  </span>
                )}
              </div>
              <div className="text-[13px] text-[#747474]">
                {it.description}
                {it.store && <> · {it.store}</>}
                {it.cost_code && <> · {it.cost_code}</>}
              </div>
            </div>
            <input
              placeholder={t("approvals.comment")}
              value={comment[it.id] || ""}
              onChange={(e) => setComment({ ...comment, [it.id]: e.target.value })}
              className="sw-input"
            />
            <div className="flex gap-2">
              <button data-testid={`approve-reimb-${it.id}`} onClick={() => decide("reimb", it.id, "approved")} className="sw-btn-primary !bg-[#008932] hover:!bg-[#0b430b]">
                <Check size={14} /> {t("approvals.approve")}
              </button>
              <button data-testid={`reject-reimb-${it.id}`} onClick={() => decide("reimb", it.id, "rejected")} className="sw-btn-secondary !text-[#881a27] !border-[#e5c1c1]">
                <X size={14} /> {t("approvals.reject")}
              </button>
            </div>
          </div>
        ))}
        {tab === "billing" && billing.map((it) => (
          <div key={it.id} ref={(el) => (rowRefs.current[it.id] = el)} data-testid={`approval-billing-${it.id}`} className={cardCls(it.id)}>
            <div>
              <div className="text-[11px] uppercase tracking-wider text-[#475569] mb-1">
                {t("approvals.billing")} · {it.month}
              </div>
              <div className="font-medium text-[15px]">
                {it.user_name} · ${parseFloat(it.snapshot?.total_usd || 0).toFixed(2)}
                <span data-testid={`external-badge-billing-${it.id}`} className="ml-2 align-middle inline-flex px-2 py-0.5 rounded-full bg-[#fcf3e2] text-[#a38d5b] text-[10px] font-semibold uppercase tracking-wide">
                  {t("ext.provider_badge", { name: it.provider_tenant_name || "" })}
                </span>
              </div>
              <div className="text-[13px] text-[#747474]">
                {it.contract_name}
                {it.snapshot?.billing_rate ? (
                  <> · {t("sc.billing_fee")}: ${parseFloat(it.snapshot.fee_usd || 0).toFixed(2)}
                    {it.snapshot.billing_rate_type === "hourly" && <> ({it.snapshot.hours}h × ${parseFloat(it.snapshot.billing_rate).toFixed(2)})</>}
                  </>
                ) : null}
                {" · "}{t("sc.billing_reimb")}: ${parseFloat(it.snapshot?.reimb_usd || 0).toFixed(2)}
                {" · "}{t("sc.billing_travel")}: ${parseFloat(it.snapshot?.travel_usd || 0).toFixed(2)}
              </div>
              {it.snapshot?.fee_department_label && (
                <div className="text-[12px] text-[#94a3b8] mt-1">{it.snapshot.fee_cost_item_label || "—"} · {it.snapshot.fee_department_label}</div>
              )}
            </div>
            <input
              placeholder={t("approvals.comment")}
              value={comment[it.id] || ""}
              onChange={(e) => setComment({ ...comment, [it.id]: e.target.value })}
              className="sw-input"
            />
            <div className="flex gap-2">
              <button data-testid={`approve-billing-${it.id}`} onClick={() => decide("billing", it.id, "approved")} className="sw-btn-primary !bg-[#008932] hover:!bg-[#0b430b]">
                <Check size={14} /> {t("approvals.approve")}
              </button>
              <button data-testid={`reject-billing-${it.id}`} onClick={() => decide("billing", it.id, "rejected")} className="sw-btn-secondary !text-[#881a27] !border-[#e5c1c1]">
                <X size={14} /> {t("approvals.reject")}
              </button>
            </div>
          </div>
        ))}
        {tab === "ts" && ts.map((it) => (
          <div key={it.id} ref={(el) => (rowRefs.current[it.id] = el)} data-testid={`approval-ts-${it.id}`} className={cardCls(it.id)}>
            <div>
              <div className="text-[11px] uppercase tracking-wider text-[#475569] mb-1">Timesheet · {it.contract_type?.replace("_", "·")}</div>
              <div className="font-medium text-[15px]">{it.user_name} · {it.total_hours}h</div>
              <div className="text-[13px] text-[#747474]">{it.period_start} → {it.period_end} · {it.entries.length} alocação(ões)</div>
            </div>
            <input
              placeholder={t("approvals.comment")}
              value={comment[it.id] || ""}
              onChange={(e) => setComment({ ...comment, [it.id]: e.target.value })}
              className="sw-input"
            />
            <div className="flex gap-2">
              <button data-testid={`approve-ts-${it.id}`} onClick={() => decide("ts", it.id, "approved")} className="sw-btn-primary !bg-[#008932] hover:!bg-[#0b430b]">
                <Check size={14} /> {t("approvals.approve")}
              </button>
              <button data-testid={`reject-ts-${it.id}`} onClick={() => decide("ts", it.id, "rejected")} className="sw-btn-secondary !text-[#881a27] !border-[#e5c1c1]">
                <X size={14} /> {t("approvals.reject")}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
